import { useAppDispatch, useAppSelector } from "../../../../store/store";
import SidebarCollapse from "../../../app/SidebarCollapse";
import { getValueUnit } from "../../../general/editUtils";
import BorderRadiusInputs from "../../../general/BorderRadiusInputs";
import { BorderRadiusInit } from "../../../general/BorderRadiusInputs";
import ColorInput from "../../../general/ColorInput";
import SelectInput from "../../../general/SelectInput";
import AddShadows from "../../../general/AddShadows";
import UnitInput from "../../../general/UnitInput";
import Button from "../../../general/Button";
import OliverText from "../../../general/OliverText";
import "../style.scss";
import CheckboxInput, { CheckboxOption } from "../../../general/CheckboxInput";
import { EditableProps } from "../../editableProps";
import { clearBorderStyles, updateNodeProperty } from "../../../../store/slices/BuilderSlice/builderSlice";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { useTranslation } from "react-i18next";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";
import { useEffect, useState } from "react";

const Borders: React.FC<EditableProps> = ({ title, path, noWrapper, condition, parent, stateMode }: EditableProps) => {
    const dispatch = useAppDispatch();
    const activeNode = useAppSelector(state => state.builder.activeNode);
    const node = useAppSelector(getSelectedNode);
    const { t } = useTranslation();
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const cvModeIndex = cvMode?.id === node?.id ? cvMode.index : null;
    const [showStrokeOptions, setShowStrokeOptions] = useState(false);

    if (!node) return null;

    const styles = getStyle(node, path, stateMode, cvModeIndex);
    const stylePath = getStylePath(path, stateMode, cvModeIndex);

    //Border Radius
    const borderRadiusSplit = (styles.stateStyle?.borderRadius || styles.default?.borderRadius)?.split(" ") || "";
    const borderRadius: BorderRadiusInit = borderRadiusSplit
        ? {
              topLeft: getValueUnit(borderRadiusSplit[0]),
              topRight: getValueUnit(borderRadiusSplit[1]),
              bottomRight: getValueUnit(borderRadiusSplit[2]),
              bottomLeft: getValueUnit(borderRadiusSplit[3])
          }
        : {
              topLeft: { value: "", unit: "px" },
              topRight: { value: "", unit: "px" },
              bottomRight: { value: "", unit: "px" },
              bottomLeft: { value: "", unit: "px" }
          };
    const borderRadiusCallback = (values: BorderRadiusInit) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...stylePath, "borderRadius"],
                value: `${values.topLeft.value + values.topLeft.unit} ${values.topRight.value + values.topRight.unit} ${
                    values.bottomRight.value + values.bottomRight.unit
                } ${values.bottomLeft.value + values.bottomLeft.unit}`
            })
        );
    };

    //Border Style
    const borderStyle = styles.stateStyle?.borderStyle || styles.default?.borderStyle || "solid";
    const borderStyleOptions = {
        solid: "Solid",
        dashed: "Dashed"
    };
    const borderStyleCallback = (value: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...stylePath, "borderStyle"],
                value
            })
        );
    };

    //Border Width
    const borderWidthString = styles.stateStyle?.borderWidth || styles.default?.borderWidth;
    const borderWidthSplit = borderWidthString?.split(" ") || "";
    const borderWidth = borderWidthSplit
        ? [getValueUnit(borderWidthSplit[0]), getValueUnit(borderWidthSplit[1]), getValueUnit(borderWidthSplit[2]), getValueUnit(borderWidthSplit[3])]
        : [
              { value: "", unit: "px" },
              { value: "", unit: "px" },
              { value: "", unit: "px" },
              { value: "", unit: "px" }
          ];
    const borderWidthSingle = borderWidth.find(width => width.value && parseInt(width.value) > 0) || { value: "", unit: "px" };
    const borderWidthCallback = (value: string, unit: string) => {
        const borderWidth = `${(value || "0") + unit} ${(value || "0") + unit} ${(value || "0") + unit} ${(value || "0") + unit}`;
        dispatch(
            updateNodeProperty({
                propertyPath: [...stylePath, "borderWidth"],
                value: borderWidth,
                remove: !value
            })
        );
        dispatch(updateNodeProperty({ propertyPath: [...stylePath, "borderStyle"], value: borderStyle, remove: !value }));
    };

    //Borders
    const borderOptions: CheckboxOption[] = [
        { value: "all", img: "/border-all.svg", checked: false, tooltip: "All borders" },
        {
            value: "top",
            img: "/border-top.svg",
            checked: borderWidth[0] && parseInt(borderWidth[0].value) > 0,
            tooltip: "Top border"
        },
        {
            value: "right",
            img: "/border-right.svg",
            checked: borderWidth[1] && parseInt(borderWidth[1].value) > 0,
            tooltip: "Right border"
        },
        {
            value: "bottom",
            img: "/border-bottom.svg",
            checked: borderWidth[2] && parseInt(borderWidth[2].value) > 0,
            tooltip: "Bottom border"
        },
        {
            value: "left",
            img: "/border-left.svg",
            checked: borderWidth[3] && parseInt(borderWidth[3].value) > 0,
            tooltip: "Left border"
        }
    ];
    let borderCounter = 0;
    borderOptions.forEach(border => {
        if (border.checked) borderCounter++;
    });
    if (borderCounter === 4) {
        borderOptions.forEach(border => {
            border.checked = false;
        });
        borderOptions[0].checked = true;
    }
    const borderCallback = (value: string, checked: boolean) => {
        switch (value) {
            case "all":
                if (checked) {
                    dispatch(
                        updateNodeProperty({
                            propertyPath: [...stylePath, "borderWidth"],
                            value: `${(borderWidthSingle.value || 1) + borderWidthSingle.unit} ${
                                (borderWidthSingle.value || 1) + borderWidthSingle.unit
                            } ${(borderWidthSingle.value || 1) + borderWidthSingle.unit} ${(borderWidthSingle.value || 1) + borderWidthSingle.unit}`
                        })
                    );
                }
                break;
            case "top":
                if (checked) {
                    if (borderOptions[0].checked) {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `${(borderWidthSingle.value || 1) + borderWidthSingle.unit} 0px 0px 0px`
                            })
                        );
                    } else {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `${(borderWidthSingle.value || 1) + borderWidthSingle.unit} ${
                                    (borderWidth[1].value || 0) + borderWidth[1].unit
                                } ${(borderWidth[2].value || 0) + borderWidth[2].unit} ${(borderWidth[3].value || 0) + borderWidth[3].unit}`
                            })
                        );
                    }
                } else {
                    dispatch(
                        updateNodeProperty({
                            propertyPath: [...stylePath, "borderWidth"],
                            value: `0px ${(borderWidth[1].value || 0) + borderWidth[1].unit} ${(borderWidth[2].value || 0) + borderWidth[2].unit} ${
                                (borderWidth[3].value || 0) + borderWidth[3].unit
                            }`
                        })
                    );
                }
                break;
            case "right":
                if (checked) {
                    if (borderOptions[0].checked) {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `0px ${(borderWidthSingle.value || 1) + borderWidthSingle.unit} 0px 0px`
                            })
                        );
                    } else {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `${(borderWidth[0].value || 0) + borderWidth[0].unit} ${
                                    (borderWidthSingle.value || 1) + borderWidthSingle.unit
                                } ${(borderWidth[2].value || 0) + borderWidth[2].unit} ${(borderWidth[3].value || 0) + borderWidth[3].unit}`
                            })
                        );
                    }
                } else {
                    dispatch(
                        updateNodeProperty({
                            propertyPath: [...stylePath, "borderWidth"],
                            value: `${(borderWidth[0].value || 0) + borderWidth[0].unit} 0px ${(borderWidth[2].value || 0) + borderWidth[2].unit} ${
                                (borderWidth[3].value || 0) + borderWidth[3].unit
                            }`
                        })
                    );
                }
                break;
            case "bottom":
                if (checked) {
                    if (borderOptions[0].checked) {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `0px 0px ${(borderWidthSingle.value || 1) + borderWidthSingle.unit} 0px`
                            })
                        );
                    } else {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `${(borderWidth[0].value || 0) + borderWidth[0].unit} ${(borderWidth[1].value || 0) + borderWidth[1].unit} ${
                                    (borderWidthSingle.value || 1) + borderWidthSingle.unit
                                } ${(borderWidth[3].value || 0) + borderWidth[3].unit}`
                            })
                        );
                    }
                } else {
                    dispatch(
                        updateNodeProperty({
                            propertyPath: [...stylePath, "borderWidth"],
                            value: `${(borderWidth[0].value || 0) + borderWidth[0].unit} ${(borderWidth[1].value || 0) + borderWidth[1].unit} 0px ${
                                (borderWidth[3].value || 0) + borderWidth[3].unit
                            }`
                        })
                    );
                }
                break;
            case "left":
                if (checked) {
                    if (borderOptions[0].checked) {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `0px 0px 0px ${(borderWidthSingle.value || 1) + borderWidthSingle.unit}`
                            })
                        );
                    } else {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: [...stylePath, "borderWidth"],
                                value: `${(borderWidth[0].value || 0) + borderWidth[0].unit} ${(borderWidth[1].value || 0) + borderWidth[1].unit} ${
                                    (borderWidth[2].value || 0) + borderWidth[2].unit
                                } ${(borderWidthSingle.value || 1) + borderWidthSingle.unit}`
                            })
                        );
                    }
                } else {
                    dispatch(
                        updateNodeProperty({
                            propertyPath: [...stylePath, "borderWidth"],
                            value: `${(borderWidth[0].value || 0) + borderWidth[0].unit} ${(borderWidth[1].value || 0) + borderWidth[1].unit} ${
                                (borderWidth[2].value || 0) + borderWidth[2].unit
                            } 0px`
                        })
                    );
                }
                break;
        }

        if (!styles.stateStyle?.borderStyle && !styles.default?.borderStyle) {
            dispatch(updateNodeProperty({ propertyPath: [...stylePath, "borderStyle"], value: borderStyle }));
        }

        if (!borderColor) {
            dispatch(updateNodeProperty({ propertyPath: [...stylePath, "borderColor"], value: "#000000" }));
        }
    };

    //Border Color
    const borderColor = styles.stateStyle?.borderColor || styles.default?.borderColor;
    const borderColorCallback = (value: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...stylePath, "borderColor"],
                value
            })
        );
    };

    //Clear Border Style
    const clearBorderStyle = () => {
        dispatch(clearBorderStyles());
    };

    useEffect(() => {
        if (borderWidthString) {
            setShowStrokeOptions(true);
        } else {
            setShowStrokeOptions(false);
        }
    }, [activeNode]);

    if (condition && (!node || !condition(node, activeNode))) return null;

    const strokeOptionsContent = (
        <>
            <CheckboxInput title="Stroke placement" options={borderOptions} callback={borderCallback} tooltips />
            <UnitInput title="Stroke weight" {...borderWidthSingle} callback={borderWidthCallback} />
            <ColorInput title="Stroke color" color={borderColor} callback={borderColorCallback} />
            <SelectInput title="Stroke style" value={borderStyle} options={borderStyleOptions} callback={borderStyleCallback} />
            <button className="style-button-clear" onClick={clearBorderStyle}>
                {t("editable.borders.Clear all styles")}
            </button>
        </>
    );

    if (noWrapper) {
        return (
            <>
                <BorderRadiusInputs init={borderRadius} callback={borderRadiusCallback} />

                <div className="color-input-wrapper">
                    <div className="color-input-row">
                        <div className="left">
                            {showStrokeOptions && (
                                <Button
                                    buttonStyle="transparent"
                                    size="full"
                                    onClick={() => setShowStrokeOptions(false)}
                                    icon={{ src: "/cross.svg" }}
                                    aria-label="Hide stroke options"
                                />
                            )}
                            <OliverText text="Stroke" size="xs" />
                        </div>
                        <div className="right">
                            {!showStrokeOptions && (
                                <Button
                                    buttonStyle="grey"
                                    size="fit"
                                    onClick={() => setShowStrokeOptions(true)}
                                    icon={{ src: "/add.svg" }}
                                    aria-label="Add stroke options"
                                />
                            )}
                        </div>
                    </div>
                    {showStrokeOptions && strokeOptionsContent}
                </div>

                <AddShadows path={path} stateMode={stateMode} />
            </>
        );
    } else {
        return (
            <SidebarCollapse title={title || "Borders"} parent={parent}>
                <BorderRadiusInputs init={borderRadius} callback={borderRadiusCallback} />

                <div className="color-input-wrapper">
                    <div className="color-input-row">
                        <div className="left">
                            {showStrokeOptions && (
                                <Button
                                    buttonStyle="transparent"
                                    size="full"
                                    onClick={() => {
                                        setShowStrokeOptions(false);
                                        clearBorderStyle();
                                    }}
                                    icon={{ src: "/cross.svg" }}
                                    aria-label="Hide stroke options"
                                />
                            )}
                            <OliverText text="Stroke" size="xs" />
                        </div>
                        <div className="right">
                            {!showStrokeOptions && (
                                <Button
                                    buttonStyle="grey"
                                    size="fit"
                                    onClick={() => setShowStrokeOptions(true)}
                                    icon={{ src: "/add.svg" }}
                                    aria-label="Add stroke options"
                                />
                            )}
                        </div>
                    </div>
                    {showStrokeOptions && strokeOptionsContent}
                </div>

                <AddShadows path={path} stateMode={stateMode} />
            </SidebarCollapse>
        );
    }
};

export default Borders;
